/**
 * ProposalItemsList - Escopo/itens da proposta em formato de cards
 *
 * Substitui a tabela crua por linhas com hierarquia tipográfica clara,
 * badge de recorrência e observações do item.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-11 v1.0.0 - Criação inicial (redesign premium da landing /p/:token)
 */

import React from 'react';
import { Check, Repeat } from 'lucide-react';
import type { PublicProposalItem } from '@/hooks/usePublicProposal';
import { formatCurrency } from './proposalFormat';

interface ProposalItemsListProps {
  items: PublicProposalItem[];
  currency: string;
}

export function ProposalItemsList({ items, currency }: ProposalItemsListProps) {
  return (
    <section className="proposal-surface proposal-reveal overflow-hidden">
      <header className="flex items-center justify-between border-b px-5 py-4 md:px-7">
        <div>
          <h2 className="text-base font-semibold">O que está incluído</h2>
          <p className="text-xs text-muted-foreground">Escopo detalhado desta proposta</p>
        </div>
        <span className="rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground">
          {items.length} {items.length === 1 ? 'item' : 'itens'}
        </span>
      </header>

      <ul className="divide-y">
        {items.map((item, index) => (
          <li
            key={`${item.product_name}-${index}`}
            className="proposal-item-row flex items-start gap-4 px-5 py-4 md:px-7 md:py-5"
          >
            <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-secondary">
              <Check className="h-3.5 w-3.5 text-foreground/70" />
            </span>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-semibold md:text-[0.95rem]">{item.product_name}</p>
                {item.recurring_price ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-accent/15 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-accent-foreground">
                    <Repeat className="h-3 w-3" />
                    Recorrente
                  </span>
                ) : null}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                {Number(item.quantity)} × {formatCurrency(item.unit_price, currency)}
                {item.recurring_price
                  ? ` · ${formatCurrency(item.recurring_price, currency)}/mês`
                  : ''}
              </p>
              {item.notes && (
                <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">{item.notes}</p>
              )}
            </div>

            <span className="whitespace-nowrap text-sm font-semibold md:text-base">
              {formatCurrency(item.total, currency)}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
